import { useMemo, useState } from 'react';

export default function ForecastLineChart({ data, title = 'Forecast', height = 250, unit = '%' }) {
  const { historical = [], forecast = [] } = data || {};
  const [tooltipData, setTooltipData] = useState(null);

  const allPoints = useMemo(() => {
    const hist = historical.map(h => ({ ...h, type: 'historical' }));
    const fore = forecast.map(f => ({ ...f, type: 'forecast' }));
    return [...hist, ...fore];
  }, [historical, forecast]);

  if (allPoints.length === 0) {
    return (
      <div className="glass p-6 flex flex-col items-center justify-center" style={{ height }}>
        <p className="text-slate-400 text-sm font-medium mb-2">No data available for forecasting</p>
        <p className="text-slate-500 text-xs text-center">Forecasts will appear once sufficient historical data is collected (7+ days)</p>
      </div>
    );
  }

  const allValues = allPoints.map(p => p.value);
  const upperValues = forecast.map(f => f.upper).filter(Boolean);
  const lowerValues = forecast.map(f => f.lower).filter(Boolean);
  const allYValues = [...allValues, ...upperValues, ...lowerValues];

  const minY = Math.floor(Math.min(...allYValues) * 0.95);
  const maxY = Math.ceil(Math.max(...allYValues) * 1.05);
  const rangeY = maxY - minY || 1;

  const padding = { top: 20, right: 20, bottom: 40, left: 50 };
  const chartWidth = 600;
  const chartHeight = height - padding.top - padding.bottom;

  const xStep = allPoints.length > 1 ? (chartWidth - padding.left - padding.right) / (allPoints.length - 1) : 0;

  function getY(val) {
    return padding.top + chartHeight - ((val - minY) / rangeY) * chartHeight;
  }

  function getX(i) {
    return padding.left + i * xStep;
  }

  // Build paths
  const historicalPath = historical.map((_, i) => `${i === 0 ? 'M' : 'L'} ${getX(i)} ${getY(historical[i].value)}`).join(' ');
  const forecastStartIdx = historical.length;
  const forecastPath = forecast.map((f, i) => {
    const idx = forecastStartIdx + i;
    return `${i === 0 ? `M ${getX(forecastStartIdx - 1)} ${getY(historical[historical.length - 1]?.value || f.value)} L` : 'L'} ${getX(idx)} ${getY(f.value)}`;
  }).join(' ');

  // Confidence band
  const bandPath = forecast.length > 0 ? (() => {
    const upperPath = forecast.map((f, i) => {
      const idx = forecastStartIdx + i;
      return `${i === 0 ? 'M' : 'L'} ${getX(idx)} ${getY(f.upper || f.value)}`;
    }).join(' ');
    const lowerPath = [...forecast].reverse().map((f, i) => {
      const idx = forecastStartIdx + forecast.length - 1 - i;
      return `L ${getX(idx)} ${getY(f.lower || f.value)}`;
    }).join(' ');
    return `${upperPath} ${lowerPath} Z`;
  })() : '';

  // Divider line between historical and forecast
  const dividerX = getX(historical.length - 1);

  return (
    <div className="glass p-4 relative">
      <h3 className="text-sm font-semibold text-slate-700 mb-3">{title}</h3>
      <div className="relative">
        <svg viewBox={`0 0 ${chartWidth} ${height}`} className="w-full" style={{ height }}>
        {/* Y-axis labels */}
        {[0, 0.25, 0.5, 0.75, 1].map(pct => {
          const val = minY + rangeY * pct;
          return (
            <g key={pct}>
              <line x1={padding.left} y1={getY(val)} x2={chartWidth - padding.right} y2={getY(val)} stroke="#e2e8f0" strokeWidth="1" />
              <text x={padding.left - 5} y={getY(val) + 4} textAnchor="end" className="text-[10px]" fill="#94a3b8">
                {Math.round(val)}{unit}
              </text>
            </g>
          );
        })}

        {/* Confidence band */}
        {bandPath && (
          <path d={bandPath} fill="var(--theme-200)" opacity="0.3" />
        )}

        {/* Divider */}
        {historical.length > 0 && forecast.length > 0 && (
          <line x1={dividerX} y1={padding.top} x2={dividerX} y2={padding.top + chartHeight} stroke="#94a3b8" strokeDasharray="4 4" strokeWidth="1" />
        )}

        {/* Historical line */}
        <path d={historicalPath} fill="none" stroke="var(--theme-500)" strokeWidth="2" />

        {/* Forecast line */}
        <path d={forecastPath} fill="none" stroke="var(--theme-400)" strokeWidth="2" strokeDasharray="6 3" />

        {/* Data points */}
        {historical.map((h, i) => (
          <circle
            key={`h-${i}`}
            cx={getX(i)}
            cy={getY(h.value)}
            r="4"
            fill="var(--theme-500)"
            style={{ cursor: 'pointer' }}
            onMouseEnter={() => {
              const x = getX(i);
              const y = getY(h.value);
              const label = typeof h.date === 'string' ? h.date : `Point ${i}`;
              setTooltipData({ x, y, label, value: h.value });
            }}
            onMouseLeave={() => setTooltipData(null)}
          />
        ))}
        {forecast.map((f, i) => (
          <circle
            key={`f-${i}`}
            cx={getX(forecastStartIdx + i)}
            cy={getY(f.value)}
            r="4"
            fill="var(--theme-400)"
            stroke="white"
            strokeWidth="1"
            style={{ cursor: 'pointer' }}
            onMouseEnter={() => {
              const x = getX(forecastStartIdx + i);
              const y = getY(f.value);
              const label = typeof f.date === 'string' ? f.date : `Forecast ${i}`;
              setTooltipData({ x, y, label, value: f.value });
            }}
            onMouseLeave={() => setTooltipData(null)}
          />
        ))}

        {/* X-axis labels (show every nth) */}
        {allPoints.filter((_, i) => i % Math.max(1, Math.floor(allPoints.length / 8)) === 0).map((p, _, arr) => {
          const idx = allPoints.indexOf(p);
          const label = typeof p.date === 'string' ? p.date.slice(5) : '';
          return (
            <text key={idx} x={getX(idx)} y={height - 5} textAnchor="middle" className="text-[9px]" fill="#94a3b8">
              {label}
            </text>
          );
        })}

        {/* Legend */}
        <line x1={padding.left} y1={height - 20} x2={padding.left + 20} y2={height - 20} stroke="var(--theme-500)" strokeWidth="2" />
        <text x={padding.left + 25} y={height - 16} className="text-[10px]" fill="#64748b">Historical</text>
        <line x1={padding.left + 90} y1={height - 20} x2={padding.left + 110} y2={height - 20} stroke="var(--theme-400)" strokeWidth="2" strokeDasharray="6 3" />
        <text x={padding.left + 115} y={height - 16} className="text-[10px]" fill="#64748b">Forecast</text>
        </svg>
        {tooltipData && (
          <div
            className="absolute bg-slate-800 text-white px-2 py-1 rounded text-xs whitespace-nowrap border border-slate-600"
            style={{
              left: `calc(${(tooltipData.x / chartWidth) * 100}% + 10px)`,
              top: `${tooltipData.y - 30}px`,
              zIndex: 10,
            }}
          >
            <div className="font-semibold">{tooltipData.label}</div>
            <div>{Math.round(tooltipData.value * 10) / 10}{unit}</div>
          </div>
        )}
      </div>
    </div>
  );
}
